import { useMemo, useRef, useState } from "react";
import BIcon from "../BIcon";
import { Link } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { humanFileSize, humanFileType } from "../../transferUtils";
import { humanFileName } from "../../utils";
import Progress from "./Progress";
import img1 from "../../assets/img1.png";
import img2 from "../../assets/img2.png";
import img3 from "../../assets/img3.png";
import img4 from "../../assets/img4.png";
// import folder from '../../assets/folder.svg'
import folderDark from "../../assets/folder-dark.svg";
import plusDark from "../../assets/plus-dark.svg";

export default function FileUpload({
  initialFiles,
  onFilesChange,
  onFiles,
  onReceiveClicked,
  progressElement,
  showProgress,
  buttonText,
  singleFile,
  disabled,
  accept,
  headless,
  children,
}) {
  const _buttonText = buttonText ?? "Transfer";

  const [files, setFiles] = useState(initialFiles || []);

  const fileInputRef = useRef();
  const folderInputRef = useRef();

  const handleFileInputChange = (e) => {
    const newFiles = [...files, ...e.target.files];
    setFiles(newFiles);
    onFilesChange && onFilesChange(newFiles);
  };

  const handlePickFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current.click();
  };

  const handleSelectFolder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    folderInputRef.current.click();
  };

  const totalFileSize = useMemo(() => {
    return files.reduce((total, file) => total + file.size, 0);
  }, [files]);

  const removeFile = (file) => {
    setFiles(files.filter((otherFile) => otherFile !== file));
  };

  const handleTransferClicked = (e) => {
    onFiles(files);
  };

  return (
    <>
      <form style={{ display: "none" }}>
        <input
          ref={fileInputRef}
          onChange={handleFileInputChange}
          type="file"
          aria-hidden="true"
          multiple={singleFile ? undefined : true}
          accept={accept}
        ></input>
        <input
          ref={folderInputRef}
          onChange={handleFileInputChange}
          type="file"
          aria-hidden="true"
          webkitdirectory="true"
        ></input>
      </form>
      <div
        className={`text-start mx-auto relative w-full sm:w-[80%] md:w-[70%] flex flex-col min-h-80 sm:min-h-64 border-dashed border border-[#12A500] ${
          headless ? "" : "rounded-2xl bg-[#12A500] bg-opacity-10   "
        } ${onReceiveClicked ? "" : ""}`}
      >
        {/* Top-right (img1) */}
        <img
          src={img1}
          className="absolute -bottom-7 sm:-top-5 sm:-bottom-auto lg:-top-5 right-[-11px] md:right-[-36px]"
        />

        {/* Bottom-left (img4) */}
        <img
          src={img4}
          className="absolute top-[-30px] sm:bottom-[-36px] sm:top-auto left-[-10px] md:left-[-20px]"
        />

        {/* Decorative bottom-right (img2) */}
        <img
          src={img2}
          className="absolute hidden sm:block bottom-[-90px] left-[-50px]   sm:bottom-[-24px] sm:left-auto sm:right-[-100px] md:right-[-144px] lg:right-[-192px]"
        />

        {/* Decorative top-left (img3) */}
        <img
          src={img3}
          className="absolute hidden sm:block top-[2.75rem] sm:left-[-150px] md:left-[-150px] lg:left-[-160px]"
        />

        <Transition
          show={files.length === 0}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 flex flex-col justify-center items-center">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-10">
              {/* Pick Files */}
              <button
                type="button"
                onClick={handlePickFiles}
                className="flex flex-col items-center group"
              >
                <div className="text-white rounded-full w-12 h-12 flex items-center justify-center">
                  <img
                    src={plusDark}
                    className="opacity-100 group-hover:opacity-100"
                  />
                </div>
                <span className="mt-2 sm:mt-4 font-normal text-white text-lg">
                  Pick Files
                </span>
              </button>

              {/* Divider */}
              <div className="text-white font-semibold"> - OR - </div>

              {/* Select Folder */}
              <button
                type="button"
                onClick={handleSelectFolder}
                className="flex flex-col items-center group"
              >
                <div className="text-white rounded w-12 h-12 flex items-center justify-center">
                  <img
                    src={folderDark}
                    className="opacity-100 group-hover:opacity-100"
                  />
                </div>
                <span className="mt-2 sm:mt-4 font-normal text-white text-lg">
                  Select Folder
                </span>
              </button>
            </div>
          </div>
        </Transition>

        <Transition show={files.length > 0}>
          <div className="grow flex flex-col p-4 transition data-[closed]:opacity-0 gap-4">
            <div className="grow max-h-56 overflow-auto">
              {files.map((file) => {
                return (
                  <div
                    className="relative px-2 py-1 overflow-hidden text-md group"
                    key={file.name + file.size + file.lastModified}
                  >
                    <div className="absolute right-0 hidden group-hover:block pe-2">
                      <button
                        type="button"
                        onClick={() => removeFile(file)}
                        className="bg-white border p-1 rounded-lg"
                      >
                        <BIcon name={"x"} center />
                      </button>
                    </div>
                    <p className="text-nowrap text-white">{humanFileName(file.name)}</p>
                    <span className="text-sm text-white">
                      {humanFileSize(file.size, true)}
                      <BIcon name={"dot"} />
                      {humanFileType(file.type)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="w-full flex justify-between">
              <div className="flex gap-2">
                {!singleFile && (
                  <>
                    <button
                      type="button"
                      className="text-sm text-white pe-3 px-2 rounded-lg border shadow hover:bg-white hover:bg-opacity-10"
                      onClick={handlePickFiles}
                    >
                      <BIcon name={"plus"} /> Files
                    </button>
                    <button
                      type="button"
                      className="text-sm text-white px-2 rounded-lg border shadow hover:bg-gray-100 hover:bg-opacity-10"
                      onClick={handleSelectFolder}
                    >
                      <BIcon name={"folder-plus"} /> Folder
                    </button>
                  </>
                )}
              </div>
              <div>
                <span className="text-gray-500 text-sm me-2 hidden sm:inline">
                  {humanFileSize(totalFileSize, true)}
                </span>
                <button
                  disabled={disabled}
                  type="button"
                  onClick={handleTransferClicked}
                  className="text-white px-2 py-1 rounded-lg shadow bg-[#12A500] hover:bg-[#12A500]"
                >
                  {_buttonText} &rarr;
                </button>
              </div>
            </div>
          </div>
        </Transition>
        <Transition show={showProgress || false}>
          <div className="rounded-2xl bg-white absolute left-0 top-0 w-full h-full p-8 flex flex-row justify-center items-center group transition data-[closed]:opacity-0">
            <div className="relative w-full h-full max-w-44 max-h-44">
              {progressElement}
            </div>
          </div>
        </Transition>
      </div>
    </>
  );
}
